/**
 * ragChatService · wrapper de la Edge Function `core-rag-chat` + queries a las tablas
 * `core.rag_chat_threads` / `core.rag_chat_messages`.
 *
 * Diseñado para que el chat flotante (iteración 2) y el agente flotante global
 * (iteración futura) consuman la misma API.
 */
import { supabase } from '@/lib/supabase';

export interface DocCitation {
    chunk_id: string;
    document_id: string;
    document_title: string;
    document_kind: string;
    similarity: number;
}

export interface ProspectMatch {
    prospect_id: string;
    name: string;
    sector: string | null;
    status: string | null;
    similarity: number;
}

/** Citation puede ser un doc o un prospect — la UI distingue por la presencia
 *  de `chunk_id` vs `prospect_id`. */
export type Citation = DocCitation | ProspectMatch;

export function isDocCitation(c: Citation): c is DocCitation {
    return 'chunk_id' in c;
}
export function isProspectMatch(c: Citation): c is ProspectMatch {
    return 'prospect_id' in c;
}

export interface ChatThread {
    id: string;
    owner_user_id: string;
    prospect_id: string | null;
    title: string;
    last_message_at: string | null;
    archived_at: string | null;
    closed_at: string | null;
    channel: 'web' | 'telegram';
    created_at: string;
    updated_at: string;
}

/** Lo que el edge guarda en `citations` del mensaje del usuario con imágenes o audio. */
export interface CitacionesDeUsuario {
    imagenes?: { cantidad: number; descripciones: (string | null)[]; sin_ver: number };
    audio?: { duration_ms: number; transcripto: boolean; motivo?: 'fallo' | 'silencio' };
}

export function esCitacionesDeUsuario(
    c: ChatMessage['citations'],
): c is CitacionesDeUsuario {
    return !!c && !Array.isArray(c) && typeof c === 'object';
}

/** Lista de citas de un mensaje (vacía si `citations` es el objeto del usuario). */
export function listaDeCitas(c: ChatMessage['citations']): Citation[] {
    return Array.isArray(c) ? c : [];
}

export interface ChatMessage {
    id: string;
    thread_id: string;
    role: 'user' | 'assistant' | 'system';
    content: string;
    /** Assistant: lista de citas. User: objeto con lo que adjuntó (imágenes o audio). */
    citations: Citation[] | CitacionesDeUsuario | null;
    model: string | null;
    error: string | null;
    created_at: string;
}

export interface SendMessageRequest {
    thread_id?: string;
    prospect_id?: string;
    question: string;
    /**
     * 'standard' | 'enhanced' | 'agent'. Si no se pasa, el server decide
     * (standard con override a enhanced si el user lo tiene activado).
     * 'agent' activa el agent loop con tools (Anexo C).
     */
    mode?: 'standard' | 'enhanced' | 'agent';
    /** Default 'web' desde el frontend. El worker de Telegram manda 'telegram'. */
    channel?: 'web' | 'telegram';
    /** Hasta 5. `base64` va sin el prefijo `data:`. Con imágenes `question` puede ir vacía. */
    images?: { mimeType: string; base64: string }[];
    /** Nota de voz grabada en el navegador. No lleva texto escrito. */
    audio?: { mimeType: string; base64: string; duration_ms: number };
}

export interface SendMessageResponse {
    ok: true;
    thread_id: string;
    message_id: string | null;
    assistant_message: string;
    citations: DocCitation[];
    prospect_matches: ProspectMatch[];
    model: string;
}

export interface SendMessageError {
    ok: false;
    error: string;
}

export async function sendChatMessage(
    payload: SendMessageRequest,
): Promise<SendMessageResponse | SendMessageError> {
    const { data, error } = await supabase.functions.invoke('core-rag-chat', { body: payload });
    if (error) {
        if (data && typeof data === 'object' && 'ok' in data) {
            return data as SendMessageResponse | SendMessageError;
        }
        return { ok: false, error: error.message };
    }
    return data as SendMessageResponse | SendMessageError;
}

/**
 * Cliente del schema `core` casteado a `any` por simplicidad — las tablas
 * `rag_chat_threads` y `rag_chat_messages` no están en `database.types.ts`
 * todavía. Cuando se generen los tipos, eliminar los casts y volver a la
 * cadena fluent tipada.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const coreSchema = supabase.schema('core') as any;

/**
 * Carga el thread más reciente del usuario para un prospect dado.
 * Si no hay ninguno, devuelve null y el chat empieza limpio.
 */
export async function getLatestThreadForProspect(
    prospectId: string,
): Promise<ChatThread | null> {
    const { data, error } = await coreSchema
        .from('rag_chat_threads')
        .select('*')
        .eq('prospect_id', prospectId)
        .is('archived_at', null)
        .order('last_message_at', { ascending: false, nullsFirst: false })
        .limit(1)
        .maybeSingle();
    if (error) {
        console.warn('[ragChat] getLatestThread error:', error.message);
        return null;
    }
    return (data as ChatThread | null) ?? null;
}

/**
 * Lista los últimos N threads (con o sin scope prospect). Si scope='prospect',
 * filtra al prospect. Si scope='global', solo threads sin prospect_id.
 * Incluye archivados? No — solo activos.
 */
export async function listRecentThreads(
    options: { scope: 'prospect'; prospectId: string }
        | { scope: 'global' },
    limit = 20,
): Promise<ChatThread[]> {
    let query = coreSchema
        .from('rag_chat_threads')
        .select('*')
        .is('archived_at', null)
        .order('last_message_at', { ascending: false, nullsFirst: false })
        .limit(limit);
    if (options.scope === 'prospect') {
        query = query.eq('prospect_id', options.prospectId);
    } else {
        // Modo libre: incluye threads web Y de Telegram (continuidad — el user
        // quiere ver desde la web lo que conversó por Telegram). No filtramos
        // por canal a propósito.
        query = query.is('prospect_id', null);
    }
    const { data, error } = await query;
    if (error) {
        console.warn('[ragChat] listRecentThreads error:', error.message);
        return [];
    }
    return (data ?? []) as ChatThread[];
}

export async function listThreadMessages(threadId: string): Promise<ChatMessage[]> {
    const { data, error } = await coreSchema
        .from('rag_chat_messages')
        .select('*')
        .eq('thread_id', threadId)
        .order('created_at', { ascending: true });
    if (error) {
        console.warn('[ragChat] listMessages error:', error.message);
        return [];
    }
    return (data ?? []) as ChatMessage[];
}

export async function archiveThread(threadId: string): Promise<void> {
    await coreSchema
        .from('rag_chat_threads')
        .update({ archived_at: new Date().toISOString() })
        .eq('id', threadId);
}

/**
 * Lista TODOS los threads del usuario (prospect-scoped + libres, web +
 * telegram), activos (sin archivar), ordenados por última actividad.
 *
 * Filtra explícito por `owner_user_id` — NO confía solo en RLS — porque el
 * superadmin tiene bypass (`agent_logs.read.all`) y un select pelado le traería
 * los chats de TODOS. La página `/sol` es personal: cada uno ve solo lo suyo.
 */
export async function listAllMyThreads(ownerId: string): Promise<ChatThread[]> {
    const { data, error } = await coreSchema
        .from('rag_chat_threads')
        .select('*')
        .eq('owner_user_id', ownerId)
        .is('archived_at', null)
        .order('last_message_at', { ascending: false, nullsFirst: false });
    if (error) {
        console.warn('[ragChat] listAllMyThreads error:', error.message);
        return [];
    }
    return (data ?? []) as ChatThread[];
}

/** Hit crudo de búsqueda de contenido (la página resuelve título + snippet). */
export interface RawMessageHit {
    id: string;
    thread_id: string;
    role: 'user' | 'assistant' | 'system';
    content: string;
    created_at: string;
}

/**
 * Busca `term` dentro del contenido de los mensajes de los threads dados
 * (los del usuario). Acota por `threadIds` en vez de confiar en RLS — así el
 * resultado es siempre del owner aunque sea superadmin. Devuelve los matches
 * crudos (la página agrupa por thread y arma el snippet).
 */
export async function searchMyChatMessages(
    threadIds: string[],
    term: string,
): Promise<RawMessageHit[]> {
    const q = term.trim();
    if (!q || threadIds.length === 0) return [];
    // Escapamos los wildcards de ILIKE para que el término sea literal.
    const safe = q.replace(/[%_\\]/g, (m) => `\\${m}`);
    const { data, error } = await coreSchema
        .from('rag_chat_messages')
        .select('id, thread_id, role, content, created_at')
        .in('thread_id', threadIds)
        .ilike('content', `%${safe}%`)
        .order('created_at', { ascending: false })
        .limit(80);
    if (error) {
        console.warn('[ragChat] searchMyChatMessages error:', error.message);
        return [];
    }
    return (data ?? []) as RawMessageHit[];
}
